import { Badge } from "@fluentui/react-components";

type BadgeColor = "warning" | "success" | "danger" | "subtle" | "informative";
type BadgeAppearance = "filled" | "outline" | "tint" | "ghost";

interface BadgeStyle {
  color: BadgeColor;
  appearance: BadgeAppearance;
  label: string;
}

const STYLE: Record<string, BadgeStyle> = {
  pending_review: { color: "warning", appearance: "filled", label: "Pending review" },
  approved: { color: "success", appearance: "filled", label: "Approved" },
  // Archived = decided as "discard / keep for audit". Neutral grey, not red:
  // it's a deliberate operator decision, not a failure.
  archived: { color: "informative", appearance: "tint", label: "Archived" },
  // Removed = actively deleted from SharePoint. Distinct from archived:
  // subtle/ghost so it doesn't visually compete with approved/pending.
  removed: { color: "subtle", appearance: "outline", label: "Removed" },
};

const FAILED_STATUSES = new Set(["classification_failed", "extraction_failed"]);

/**
 * Badge for a single document, derived from its `status` and `approvalStatus`.
 * Failed processing trumps approval state.
 */
export function ApprovalBadge({
  status,
  approvalStatus,
  archiveOnly,
}: {
  status: string;
  approvalStatus: string;
  archiveOnly?: boolean;
}) {
  if (archiveOnly) {
    return (
      <Badge appearance="filled" color="informative">
        Archive only
      </Badge>
    );
  }
  if (FAILED_STATUSES.has(status)) {
    return (
      <Badge appearance="filled" color="danger">
        Failed
      </Badge>
    );
  }
  const style = STYLE[approvalStatus] ?? {
    color: "subtle" as const,
    appearance: "outline" as const,
    label: approvalStatus,
  };
  return (
    <Badge
      appearance={style.appearance}
      color={style.color}
      style={
        approvalStatus === "removed"
          ? { textDecoration: "line-through" }
          : undefined
      }
    >
      {style.label}
    </Badge>
  );
}
